import { getClient } from "@lib/sanity";
import { ProductMain, SanityProductPreset } from "@sourceful/shared-types";
import {
  SanityPresetFetcher,
  SanityProductFetcher,
  toBrowserLocale,
} from "@sourceful/shared-utils/sanity-utils";
import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from "next";
import { i18n } from "next-i18next.config";
import ProductDetailPage from "views/productDetailPage";

interface ProductDetailProps {
  product: ProductMain;
  presets: SanityProductPreset[];
}

function ProductDetail({ product, presets }: ProductDetailProps) {
  return <ProductDetailPage key={product.id} product={product} presets={presets} />;
}

export default ProductDetail;

export const getStaticPaths: GetStaticPaths = async () => {
  const groqClient = getClient(false);
  const productFetcher = new SanityProductFetcher({ client: groqClient, locale: "" }); // locale isn't needed for fetchProductSlugs method
  const slugs = await productFetcher.fetchProductSlugs();

  const paths: GetStaticPathsResult["paths"] = slugs.map(slug => ({
    params: { productMain: slug.slug },
    locale: toBrowserLocale(slug.locale), // Important: convert between sanity locale format to next locale format
  }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({
  locale = i18n.defaultLocale,
  params,
  preview = false,
}) => {
  const productMain = params?.productMain!;
  const groqClient = getClient(preview);

  const productFetcher = new SanityProductFetcher({
    client: groqClient,
    locale,
    isPreview: !!preview,
  });
  const product = await productFetcher.fetchBySlug({
    slug: String(productMain),
    activeOnly: !preview, // product detail pages are created for active products only unless in preview mode
  });

  if (!product) {
    return {
      redirect: {
        destination: "/404",
        permanent: false,
      },
    };
  }

  const presetFetcher = new SanityPresetFetcher({ locale, client: groqClient });
  const presets = await presetFetcher.fetchPresetsForProduct({
    baseProductId: product.baseProductId,
    versionId: product.versionId,
  });

  return {
    props: {
      product,
      presets: presets || [],
    },
  };
};
